<template>
  <nav class="navbar navbar-expand-md navbar-light d-none d-md-flex fixed-top" id="topbar">
    <div class="container-fluid justify-content-end">
      <a class="navbar-brand" href="/">
        <img src="/img/univ-clear (2).png" class="navbar-brand-img mx-auto" alt="Logo" />
      </a>

      <div class="navbar-user d-flex align-items-center">
        <div class="notification-wrapper position-relative me-4">
          <i class="fe fe-bell fs-4" @click.stop="toggleDropdown"></i>

          <span v-if="unreadCount" class="badge-indicator">{{ unreadCount }}</span>

          <transition name="fade">
            <div v-if="showDropdown" class="notification-dropdown shadow rounded" @click.stop>
              <div
                class="dropdown-header fw-bold px-3 py-3 d-flex justify-content-between align-items-center sticky-top bg-white border-bottom"
              >
                <h4 class="my-0">Notifications</h4>
                <button
                  v-if="unreadCount"
                  type="button"
                  class="btn btn-link btn-sm p-0"
                  :disabled="isMarkingAllRead"
                  @click.stop="markAllAsReadHandler"
                >
                  Tandai semua dibaca
                </button>
              </div>

              <b-tabs v-model="activeTabIndex" fill nav-class="custom-tabs sticky-top bg-white border-bottom">
                <b-tab v-for="tab in tabs" :key="tab.key" :title="tab.label"></b-tab>
              </b-tabs>

              <div class="tab-content notif-scroll">
                <template v-if="isLoading">
                  <div class="p-3 text-center text-muted small">Loading notifications...</div>
                </template>

                <template v-else-if="filteredNotifications?.length">
                  <div
                    v-for="notif in filteredNotifications"
                    :key="notif.id"
                    class="notification-item position-relative"
                    :class="{ 'bg-light-danger': !notif.is_read }"
                    @click="markAsReadHandler(notif)"
                  >
                    <span v-if="!notif.is_read" class="notif-dot"></span>
                    <div class="p-3">
                      <div class="text-dark small fw-bold mb-1">{{ notif.title }}</div>
                      <div class="text-dark small mb-2">{{ notif.description }}</div>

                      <b-button size="sm" class="mb-2 px-3 py-1 rounded" variant="primary" @click.stop="openNotif(notif)">
                        View
                      </b-button>

                      <div class="text-muted small">{{ formatDate(notif.created_at) }}</div>
                    </div>
                  </div>
                </template>

                <div v-else class="p-3 text-center text-muted small">No notifications</div>
              </div>
            </div>
          </transition>
        </div>

        <b-dropdown right variant="muted" no-caret boundary="window">
          <template #button-content>
            <div class="d-flex align-items-center">
              <i class="fe fe-user me-1"></i>
              {{ getUserName() }}
              <i class="fe fe-chevron-down ms-1"></i>
            </div>
          </template>

          <b-dropdown-item @click.prevent="goProfile">Profile</b-dropdown-item>
          <b-dropdown-item @click.prevent="logout">Logout</b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
  </nav>
</template>

<script>
import querystring from "query-string";
import axios from "axios";
import { mapActions, mapGetters } from "vuex";

export default {
  data() {
    return {
      filters: {
        page: 1,
        pageSize: 10,
        order: "desc",
        module_type_id: null,
      },
      showDropdown: false,
      activeTabIndex: 0,
      isLoading: false,
      isMarkingAllRead: false,
      tabs: [{ key: "all", label: "All" }],
    };
  },

  computed: {
    ...mapGetters("header", ["notifications", "notificationCounts"]),

    unreadCount() {
      return this.notificationCounts > 10 ? "10+" : this.notificationCounts || 0;
    },

    currentTabKey() {
      return this.tabs[this.activeTabIndex]?.key || "all";
    },

    filteredNotifications() {
      return this.notifications?.data || [];
    },
  },

  watch: {
    async activeTabIndex() {
      const activeKey = this.currentTabKey;
      this.filters.module_type_id = activeKey === "all" ? null : activeKey;
      await this.fetchNotifications();
    },
  },

  methods: {
    ...mapActions("header", ["getNotifications", "getNotificationCounts", "markAsRead", "markAllAsRead", "getNotificationTypes"]),

    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },

    buildParams() {
      const params = {};
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          params[key] = value;
        }
      });
      return params;
    },

    async fetchNotifications() {
      this.isLoading = true;
      try {
        const params = this.buildParams();
        await this.getNotifications(params);
      } catch (err) {
        console.error("Error fetching notifications:", err);
      } finally {
        this.isLoading = false;
      }
    },

    async markAsReadHandler(notif) {
      if (notif.is_read) return;
      try {
        await this.markAsRead(notif.id);
        notif.is_read = true;
        await this.getNotificationCounts();
        await this.fetchNotifications();
      } catch (err) {
        console.error("Error marking notification as read:", err);
      }
    },

    async markAllAsReadHandler() {
      if (this.isMarkingAllRead) return;
      this.isMarkingAllRead = true;
      try {
        await this.markAllAsRead();
        await Promise.all([this.getNotificationCounts(), this.fetchNotifications()]);
      } catch (err) {
        console.error("Error marking all notifications as read:", err);
      } finally {
        this.isMarkingAllRead = false;
      }
    },

    openNotif(notif) {
      if (!notif.is_read) this.markAsReadHandler(notif);
      const targetRoute = notif.link || "/dashboard";
      if (/^https?:\/\//i.test(targetRoute)) {
        window.open(targetRoute, "_blank");
      } else {
        this.$router.push(targetRoute).catch(() => {});
      }
      this.showDropdown = false;
    },

    formatDate(date) {
      const d = new Date(date);
      const now = new Date();

      const dLocal = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const nowLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const diffDays = Math.round((nowLocal - dLocal) / 86400000);
      const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

      if (diffDays === 0) return `Today at ${time}`;
      if (diffDays === 1) return `Yesterday at ${time}`;

      return `${d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })} at ${time}`;
    },

    getUserName() {
      return JSON.parse(localStorage.getItem("ugems-tokenParsed") || "{}").given_name || "Unknown";
    },

    goProfile() {
      this.$router.push("/profile");
    },

    async logout() {
      try {
        const logoutUrl = `${process.env.VUE_APP_KEYCLOAK_URL}/realms/${
          process.env.VUE_APP_KEYCLOAK_REALM || "ugems"
        }/protocol/openid-connect/logout`;

        await axios.post(
          logoutUrl,
          querystring.stringify({
            client_id: process.env.VUE_APP_KEYCLOAK_CLIENT_ID || "url-shortener",
            refresh_token: localStorage.getItem("ugems-refreshToken"),
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: `Bearer ${localStorage.getItem("ugems-accessToken")}`,
            },
          }
        );

        localStorage.clear();
        this.$router.push("/dashboard");
        window.location.reload();
      } catch (err) {
        console.error("Logout error:", err);
      }
    },

    closeDropdown(e) {
      if (!this.$el.contains(e.target)) this.showDropdown = false;
    },
  },

  async mounted() {
    document.addEventListener("click", this.closeDropdown);
    this.isLoading = true;
    try {
      const [types] = await Promise.all([this.getNotificationTypes(), this.getNotificationCounts()]);

      if (Array.isArray(types) && types.length) {
        this.tabs = [
          { key: "all", label: "All" },
          ...types.map((t) => ({
            key: String(t.id),
            label: t.name,
          })),
        ];
      }

      await this.fetchNotifications();
    } catch (err) {
      console.error("Failed to init notifications:", err);
    } finally {
      this.isLoading = false;
    }
  },

  beforeDestroy() {
    document.removeEventListener("click", this.closeDropdown);
  },
};
</script>

<style scoped>
.notification-wrapper {
  position: relative;
  cursor: pointer;
}

.notification-badge {
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(50%, -50%);
  background-color: #dc3545;
  color: #fff;
  font-size: 0.65rem;
  font-weight: bold;
  min-width: 10px;
  height: 10px;
  line-height: 10px;
  border-radius: 50%;
  text-align: center;
  padding: 1px;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
}

.badge-indicator {
    position: absolute;
    top: -10px; /* Adjust vertical position */
    left: 11px; /* Adjust horizontal position */
    height: 20px; /* Controls the size of the badge */
    min-width: 20px; /* Ensures the badge is round even with single digits */
    padding: 0 6px;
    color: #fff; /* Text color */
    font-size: 12px;
    line-height: 20px; /* Vertically centers the text */
    white-space: nowrap;
    text-align: center;
    background: #ff4d4f; /* Ant Design's default red color */
    border-radius: 10px; /* Makes it a circle/oval */
    box-shadow: 0 0 0 1px #fff; /* Small white border for contrast */
    z-index: 1;
    /* Smooth transition for when the count changes or the badge appears/disappears */
    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1); 
}


.notification-dropdown {
  position: absolute;
  top: 220%;
  right: 0;
  width: 400px;
  background: #fff;
  border-radius: 8px;
  z-index: 2;
  max-height: 500px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.notif-scroll {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
}
.notif-scroll::-webkit-scrollbar {
  display: none;
}

::v-deep(.custom-tabs .nav-link) {
  padding: 8px 0 !important;
  border: none;
  color: #6c757d;
}

::v-deep(.custom-tabs .nav-link.active) {
  color: #000;
  border-bottom: 2px solid #dc3545;
  font-weight: 600;
}

.notif-dot {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: #dc3545;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.notification-item {
  background: #fff;
  transition: all 0.2s ease;
}

.notification-item:hover {
  background: #f8f9fa;
}

.bg-light-danger {
  background-color: #f8d7da !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
